import { useState, useMemo } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Download, FileDown, Landmark, RefreshCw, History, FileText } from "lucide-react";

function formatCurrency(valor: number): string {
  return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("pt-BR");
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("pt-BR");
}

export default function BankReceipts() {
  const { user } = useGoogleAuth();
  const emitenteId = user?.emitenteId;

  const [selectedContaId, setSelectedContaId] = useState<number | null>(null);
  const [selectedBoletos, setSelectedBoletos] = useState<Set<number>>(new Set());
  const [gerandoCnab, setGerandoCnab] = useState(false);

  // Buscar emitentes (para admin sem emitenteId fixo)
  const { data: emitentesData } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !emitenteId,
  });
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);
  const emitenteIdEfetivo = emitenteId ?? selectedEmitenteId;

  // Buscar contas bancárias do emitente
  const { data: contasBancarias } = trpc.bankAccounts.list.useQuery(
    { emitenteId: emitenteIdEfetivo ?? undefined },
    { enabled: !!emitenteIdEfetivo }
  );

  // Buscar boletos pendentes de remessa da conta selecionada
  const { data: boletosData, refetch: refetchBoletos } = trpc.boletos.listPendentes.useQuery(
    {
      contaBancariaId: selectedContaId ?? undefined,
      emitenteId: emitenteIdEfetivo ?? undefined,
      status: "gerado",
    },
    { enabled: !!selectedContaId }
  );

  // Buscar histórico de remessas
  const { data: remessasData, refetch: refetchRemessas } = trpc.boletos.listRemessas.useQuery(
    {
      emitenteId: emitenteIdEfetivo ?? undefined,
      contaBancariaId: selectedContaId ?? undefined,
    },
    { enabled: !!emitenteIdEfetivo }
  );

  const boletos = boletosData?.data ?? [];
  const remessas = remessasData?.data ?? [];

  // Selecionar/desselecionar todos
  const allSelected = boletos.length > 0 && selectedBoletos.size === boletos.length;
  const someSelected = selectedBoletos.size > 0 && !allSelected;

  function toggleAll() {
    if (allSelected) {
      setSelectedBoletos(new Set());
    } else {
      setSelectedBoletos(new Set(boletos.map((b: any) => b.id)));
    }
  }

  function toggleBoleto(id: number) {
    setSelectedBoletos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Conta bancária selecionada
  const contaSelecionada = useMemo(
    () => contasBancarias?.find((c: any) => c.id === selectedContaId),
    [contasBancarias, selectedContaId]
  );

  // Mutation para gerar CNAB
  const gerarCnabMutation = trpc.boletos.gerarCnab.useMutation({
    onSuccess: (data) => {
      // Download automático do arquivo
      const bytes = Uint8Array.from(atob(data.conteudo), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(`Remessa gerada com sucesso! ${data.totalBoletos} boleto(s) incluído(s).`);
      setSelectedBoletos(new Set());
      refetchBoletos();
      refetchRemessas();
      setGerandoCnab(false);
    },
    onError: (err) => {
      toast.error("Erro ao gerar remessa: " + err.message);
      setGerandoCnab(false);
    },
  });

  function handleGerarCnab() {
    if (!selectedContaId || !emitenteIdEfetivo) {
      toast.error("Selecione a conta bancária.");
      return;
    }
    if (selectedBoletos.size === 0) {
      toast.error("Selecione ao menos um boleto para incluir na remessa.");
      return;
    }
    setGerandoCnab(true);
    gerarCnabMutation.mutate({
      contaBancariaId: selectedContaId,
      emitenteId: emitenteIdEfetivo,
      boletoIds: Array.from(selectedBoletos),
    });
  }

  // Valor total dos boletos selecionados
  const valorTotalSelecionado = boletos
    .filter((b: any) => selectedBoletos.has(b.id))
    .reduce((acc: number, b: any) => acc + Number(b.valor), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Remessas Recebimento</h1>
            <p className="text-sm text-muted-foreground">Geração de arquivos CNAB 400 para cobrança bancária</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Emitente (apenas para admin sem emitenteId fixo) */}
            {!emitenteId && emitentesData && (
              <div className="flex flex-col gap-1 min-w-[220px]">
                <label className="text-sm font-medium">Emitente</label>
                <Select
                  value={selectedEmitenteId ? String(selectedEmitenteId) : ""}
                  onValueChange={(v) => {
                    setSelectedEmitenteId(parseInt(v));
                    setSelectedContaId(null);
                    setSelectedBoletos(new Set());
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    {emitentesData.map((e: any) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Conta Bancária */}
            <div className="flex flex-col gap-1 min-w-[280px]">
              <label className="text-sm font-medium">Conta Bancária</label>
              <Select
                value={selectedContaId ? String(selectedContaId) : ""}
                onValueChange={(v) => {
                  setSelectedContaId(parseInt(v));
                  setSelectedBoletos(new Set());
                }}
                disabled={!emitenteIdEfetivo}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta bancária" />
                </SelectTrigger>
                <SelectContent>
                  {(contasBancarias ?? []).map((c: any) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.nomeBanco} ({c.codigoBanco}) — Ag. {c.agencia} / Cta. {c.conta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Botão Gerar Remessa */}
            <Button
              onClick={handleGerarCnab}
              disabled={selectedBoletos.size === 0 || gerandoCnab}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {gerandoCnab ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Gerando...</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" /> Gerar Remessa CNAB ({selectedBoletos.size})</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de boletos pendentes */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Boletos Pendentes de Remessa
              {boletos.length > 0 && (
                <Badge variant="secondary">{boletos.length}</Badge>
              )}
            </CardTitle>
            {selectedBoletos.size > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedBoletos.size} selecionado(s) · {formatCurrency(valorTotalSelecionado)}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!selectedContaId ? (
            <p className="text-center text-muted-foreground py-8">
              Selecione uma conta bancária para ver os boletos pendentes.
            </p>
          ) : boletos.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum boleto pendente de remessa para esta conta.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 w-10">
                      <Checkbox
                        checked={allSelected}
                        ref={(el) => {
                          if (el) (el as any).indeterminate = someSelected;
                        }}
                        onCheckedChange={toggleAll}
                      />
                    </th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">ID</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Parcela</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Destinatário</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Vencimento</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Valor</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Nosso Número</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {boletos.map((b: any) => (
                    <tr
                      key={b.id}
                      className={`border-b hover:bg-muted/40 cursor-pointer transition-colors ${selectedBoletos.has(b.id) ? "bg-blue-50" : ""}`}
                      onClick={() => toggleBoleto(b.id)}
                    >
                      <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedBoletos.has(b.id)}
                          onCheckedChange={() => toggleBoleto(b.id)}
                        />
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{b.id}</td>
                      <td className="py-2 px-3">
                        {b.numeroParcela && b.totalParcelas
                          ? `${b.numeroParcela}/${b.totalParcelas}`
                          : "-"}
                      </td>
                      <td className="py-2 px-3 font-medium">{b.destinatarioNome ?? "-"}</td>
                      <td className="py-2 px-3">{formatDate(b.dataVencimento)}</td>
                      <td className="py-2 px-3 text-right font-medium">{formatCurrency(b.valor)}</td>
                      <td className="py-2 px-3 font-mono text-xs">{b.nossoNumero}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-yellow-700 border-yellow-400 bg-yellow-50">
                          Pendente
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de remessas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-5 w-5 text-gray-500" />
            Histórico de Remessas Geradas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {remessas.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">
              Nenhuma remessa gerada ainda.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Data</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Banco</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Conta</th>
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Arquivo</th>
                    <th className="text-center py-2 px-3 text-muted-foreground font-medium">Boletos</th>
                    <th className="text-center py-2 px-3 text-muted-foreground font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {remessas.map((r: any) => (
                    <tr key={r.id} className="border-b hover:bg-muted/40">
                      <td className="py-2 px-3 text-muted-foreground">{formatDateTime(r.createdAt)}</td>
                      <td className="py-2 px-3 font-medium">{r.nomeBanco} ({r.codigoBanco})</td>
                      <td className="py-2 px-3">
                        {r.nomeEmpresa ? `${r.nomeEmpresa} — ` : ""}Ag. {r.agencia} / Cta. {r.conta}
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{r.filename}</td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant="secondary">{r.totalBoletos}</Badge>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <a
                          href={r.filepath}
                          download={r.filename}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          <Download className="h-3 w-3" />
                          Baixar
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
